// Стек навигации SCR-/MOD-/OVL- по карте ux/02 (§2 уровни, §2.1 правила глубины, §3 BackButton).
// Чистая логика без DOM: экраны рендерит ui/App по состоянию, BackButton Telegram — nav/bottom-bar.
// Проверяется автотестом стека (ux/02 §2.1 п. 7): «после N раз Back стек и кнопки равны исходным».

export type Tab = 'home' | 'map' | 'coop' | 'quests' | 'shop';

/** Уровни экранов прототипа (ux/02 §2.2). `scene` — бой: навигация и полоса действия скрыты. */
export const SCREENS = {
  'SCR-HOME': { level: 0 },
  'SCR-MAP': { level: 1 },
  'SCR-STORE': { level: 1 },
  'SCR-CHAR': { level: 2 },
  'SCR-INVENTORY': { level: 3 },
  'SCR-FIGHT': { level: 2, scene: true },
  'SCR-FIGHT-RESULT': { level: 2, scene: true },
  'SCR-SETTINGS': { level: 0, service: true },
  'SCR-ACCOUNT': { level: 0, service: true },
  'SCR-SUPPORT': { level: 0, service: true },
  'SCR-LEGAL': { level: 0, service: true },
  'SCR-CAR': { level: 2 },
  'SCR-TRIPS': { level: 3 },
  'SCR-GARAGE-VISIT': { level: 2 },
  'SCR-COOP': { level: 1 },
  'SCR-COOP-SEARCH': { level: 2 },
  'SCR-COOP-CREATE': { level: 2 },
  'SCR-COOP-MEMBERS': { level: 2 },
  'SCR-COOP-REQUESTS': { level: 2 },
  'SCR-COOP-SETTINGS': { level: 2 },
  'SCR-COOP-BUILD': { level: 2 },
  'SCR-COOP-STORAGE': { level: 2 },
  'SCR-COOP-CHAT': { level: 2 },
  // Магазин и предметы (фронтенд-2): история покупок
  'SCR-PURCHASES': { level: 2 },
  'SCR-SETS': { level: 3 },
  'SCR-QUESTS': { level: 1 },
  'SCR-MAIL': { level: 2 },
  'SCR-FRIENDS': { level: 2 },
} as const satisfies Record<string, { level: 0 | 1 | 2 | 3; scene?: boolean; service?: boolean }>;
export type ScreenId = keyof typeof SCREENS;

/** Окна прототипа. ⛔ (blocking) не закрываются тапом по затемнению и Back (ux/02 §3). */
export const MODALS = {
  'MOD-BOSS': {},
  'MOD-BOSS-LOCKED': {},
  'MOD-ENERGY': {},
  'MOD-RESOURCE-INFO': {},
  'MOD-REWARD': {},
  'MOD-STATS': {},
  'MOD-LEVEL-UP': {},
  'MOD-ITEM': {},
  'MOD-UPGRADE': {},
  'MOD-DISMANTLE': { blocking: true },
  'MOD-PURCHASE': {},
  'MOD-PAY-PENDING': { blocking: true },
  'MOD-PAY-SUCCESS': {},
  'MOD-PAY-ERROR': {},
  'MOD-CONTRIBUTION': {},
  'MOD-FIGHT-LEAVE': { blocking: true },
  'MOD-CONFIRM': { blocking: true },
  'MOD-TUT-DONE': { blocking: true },
  'MOD-SESSION-ELSEWHERE': { blocking: true },
  'MOD-CONSENT': { blocking: true },
  'MOD-ZONE': {},
  'MOD-ZONE-UPGRADED': {},
  'MOD-CAR-NODE': {},
  'MOD-TRIP-START': {},
  'MOD-TRIP-RESULT': {},
  'MOD-PLAYER-CARD': {},
  'MOD-COOP-PREVIEW': {},
  'MOD-MEMBER': {},
  'MOD-COOP-LEAVE': { blocking: true },
  'MOD-COOP-DISBAND-VOTE': {},
  'MOD-COOP-DONATE': {},
  'MOD-COOP-SHOP': {},
  'MOD-CHAT-MESSAGE': {},
  'MOD-TG-GROUP': {},
  // Магазин и предметы (фронтенд-2): термос, возврат, детали заказа, витрина, ящики, шансы
  'MOD-THERMOS': {},
  'MOD-REFUND-NOTICE': {},
  'MOD-PURCHASE-DETAILS': {},
  'MOD-SHOWCASE': {},
  'MOD-BOX-OPEN': {},
  'MOD-ODDS': {},
  'MOD-S8-TOKENS': {},
  'MOD-TRANSFER': { blocking: true },
  // Карта D1–D3 (фронтенд-2, E04-T07)
  'MOD-COOP-BOSS-CALL': {},
  'MOD-INSTANCE-RESULT': {},
  'MOD-MAIL-LETTER': {},
  // Друзья и «Ещё» (фронтенд-2, E10-T04)
  'MOD-INVITE': {},
  'MOD-MORE': {},
} as const satisfies Record<string, { blocking?: boolean }>;
export type ModalId = keyof typeof MODALS;

/** Системные экраны вне стека (ux/02 §2.2). */
export type SystemId =
  | 'SCR-BOOT' | 'SCR-UNSUPPORTED' | 'SCR-UPDATE-REQUIRED' | 'SCR-MAINTENANCE' | 'SCR-BANNED'
  | 'SCR-NO-NETWORK' | 'SCR-FATAL' | 'SCR-TUT-LETTER';

export type Params = Record<string, unknown>;

export interface ScreenEntry { id: ScreenId; params: Params; key: number; savedSheets?: SheetEntry[] }
/** Содержимое листа: окно или «гостевой» экран полноразмерным листом (ux/02 §2.1 п. 2). */
export interface SheetEntry { id: ModalId | ScreenId; params: Params; key: number; guest?: boolean }

export interface NavState {
  tab: Tab;
  stack: ScreenEntry[];
  /** Логически стек, визуально один лист: следующий шаг заменяет содержимое (ux/02 §2.1 п. 5). */
  sheets: SheetEntry[];
  /** Окно поверх листа: подтверждение ⛔ (исключение из «одного окна»). */
  confirm: SheetEntry | null;
  /** Служебный стек настроек поверх текущего экрана (ux/02 §2.1 п. 3). */
  service: ScreenEntry[];
  system: { id: SystemId; params: Params } | null;
  /** Обучение T01–T04: BackButton скрыта; T05+: Back не выводит из шага (ux/11 §1.1). */
  tutorial: { step: string; navHidden: boolean; backLocked: boolean } | null;
}

export const TAB_ROOT: Record<Tab, ScreenId> = { home: 'SCR-HOME', map: 'SCR-MAP', coop: 'SCR-COOP', quests: 'SCR-QUESTS', shop: 'SCR-STORE' };
export const MAX_DEPTH = 3;

export function isBlocking(id: ModalId | ScreenId): boolean {
  return id in MODALS && !!(MODALS[id as ModalId] as { blocking?: boolean }).blocking;
}

export function initialNav(): NavState {
  return { tab: 'home', stack: [], sheets: [], confirm: null, service: [], system: { id: 'SCR-BOOT', params: {} }, tutorial: null };
}

type Listener = (s: NavState) => void;

export class Navigator {
  private s: NavState;
  private seq = 0;
  private listeners = new Set<Listener>();
  /** Перехват Back экраном (бой → MOD-FIGHT-LEAVE). Возвращает true, если обработал. */
  private interceptors = new Map<number, () => boolean>();

  constructor(initial: NavState = initialNav()) {
    this.s = initial;
  }

  get state(): NavState { return this.s; }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private set(next: Partial<NavState>) {
    this.s = { ...this.s, ...next };
    for (const l of this.listeners) l(this.s);
  }

  private nextKey() { return ++this.seq; }

  /** Текущий экран (верх стека или корень вкладки). */
  top(): ScreenId {
    return this.s.stack.at(-1)?.id ?? TAB_ROOT[this.s.tab];
  }

  topEntry(): ScreenEntry | undefined { return this.s.stack.at(-1); }

  /** Глубина полноэкранных уровней над L0. */
  depth(): number {
    return (this.s.tab === 'home' ? 0 : 1) + this.s.stack.length;
  }

  showSystem(id: SystemId, params: Params = {}) { this.set({ system: { id, params } }); }
  hideSystem() { this.set({ system: null }); }

  setTutorial(t: NavState['tutorial']) { this.set({ tutorial: t }); }

  /** Вкладка: неактивная — её корень, стек другой сбрасывается; активная на L2+ — назад в корень (ux/02 §1). */
  selectTab(tab: Tab): 'switched' | 'root' | 'scrollTop' {
    if (tab !== this.s.tab) {
      this.set({ tab, stack: [], sheets: [], confirm: null });
      return 'switched';
    }
    if (this.s.stack.length > 0) {
      this.set({ stack: [], sheets: [], confirm: null });
      return 'root';
    }
    return 'scrollTop';
  }

  /**
   * Полноэкранный экран. Четвёртый уровень невозможен: такой экран открывается гостевым листом (ux/02 §2.1 п. 1).
   * Открытые окна сворачиваются и запоминаются, Back с экрана их восстановит (п. 6).
   */
  push(id: ScreenId, params: Params = {}): void {
    if (SCREENS[id].level === 1) {
      this.selectTab(id === 'SCR-MAP' ? 'map' : id === 'SCR-COOP' ? 'coop' : id === 'SCR-QUESTS' ? 'quests' : 'shop');
      return;
    }
    if (this.depth() >= MAX_DEPTH) {
      this.openSheet(id, params, { guest: true });
      return;
    }
    const entry: ScreenEntry = { id, params, key: this.nextKey() };
    if (this.s.sheets.length) entry.savedSheets = this.s.sheets;
    this.set({ stack: [...this.s.stack, entry], sheets: [], confirm: null });
  }

  /** Замена верхнего экрана: итог боя замещает бой (ux/02 §2.2). */
  replace(id: ScreenId, params: Params = {}): void {
    const prev = this.s.stack.at(-1);
    const entry: ScreenEntry = { id, params, key: this.nextKey() };
    if (prev?.savedSheets) entry.savedSheets = prev.savedSheets;
    this.set({ stack: [...this.s.stack.slice(0, -1), entry], sheets: [], confirm: null });
  }

  pop(): void {
    const top = this.s.stack.at(-1);
    if (!top) {
      if (this.s.tab !== 'home') this.set({ tab: 'home', sheets: [], confirm: null });
      return;
    }
    this.interceptors.delete(top.key);
    this.set({ stack: this.s.stack.slice(0, -1), sheets: top.savedSheets ?? [], confirm: null });
  }

  /** Окно. Если лист уже открыт — новое содержимое того же листа; Back вернёт предыдущее (п. 5). */
  openSheet(id: ModalId | ScreenId, params: Params = {}, o: { guest?: boolean; replace?: boolean } = {}): void {
    const entry: SheetEntry = { id, params, key: this.nextKey(), ...(o.guest ? { guest: true } : {}) };
    // Подтверждения ⛔ ложатся поверх листа (п. 5, исключение); разбор из MOD-ITEM — тоже подтверждение.
    if (id === 'MOD-CONFIRM' || id === 'MOD-FIGHT-LEAVE' || (id === 'MOD-DISMANTLE' && this.s.sheets.length > 0)) {
      this.set({ confirm: entry });
      return;
    }
    const base = o.replace ? this.s.sheets.slice(0, -1) : this.s.sheets;
    this.set({ sheets: [...base, entry] });
  }

  closeSheets(): void { this.set({ sheets: [], confirm: null }); }
  closeConfirm(): void { this.set({ confirm: null }); }
  /** Закрыть верхнее содержимое листа (кнопка окна, затемнение у не-⛔). */
  popSheet(): void { this.set({ sheets: this.s.sheets.slice(0, -1) }); }

  openService(id: ScreenId = 'SCR-SETTINGS', params: Params = {}): void {
    this.set({ service: [...this.s.service, { id, params, key: this.nextKey() }] });
  }

  /** Перехват Back верхним экраном; снимается при уходе экрана. */
  intercept(key: number, fn: (() => boolean) | null): void {
    if (fn) this.interceptors.set(key, fn);
    else this.interceptors.delete(key);
  }

  /** Видна ли BackButton Telegram (ux/02 §3). */
  backVisible(): boolean {
    const s = this.s;
    if (s.system) return false;
    if (s.confirm) return !isBlocking(s.confirm.id);
    const sheet = s.sheets.at(-1);
    if (sheet) return !isBlocking(sheet.id);
    if (s.service.length) return true;
    if (s.tutorial?.backLocked) return false;
    return s.stack.length > 0 || s.tab !== 'home';
  }

  /** Нижняя навигация: L0–L2, кроме сцен, служебного стека и обучения T01–T06 (ux/02 §1–§2). */
  navVisible(): boolean {
    const s = this.s;
    if (s.system || s.service.length || s.tutorial?.navHidden) return false;
    const def: { level: number; scene?: boolean } = SCREENS[this.top()];
    return !def.scene && def.level <= 2;
  }

  /** Обработчик BackButton и аппаратной «Назад». Возвращает, что произошло, — для тестов. */
  back(): 'none' | 'confirm' | 'sheet' | 'service' | 'intercepted' | 'pop' | 'home' {
    const s = this.s;
    if (!this.backVisible()) return 'none';
    if (s.confirm) { this.closeConfirm(); return 'confirm'; }
    if (s.sheets.length) { this.popSheet(); return 'sheet'; }
    if (s.service.length) { this.set({ service: s.service.slice(0, -1) }); return 'service'; }
    const top = s.stack.at(-1);
    if (top) {
      const fn = this.interceptors.get(top.key);
      if (fn?.()) return 'intercepted';
      this.pop();
      return 'pop';
    }
    this.set({ tab: 'home' });
    return 'home';
  }
}
